<template>
  <div v-if="error && !data" class="clinic-map__error" role="alert">
    <p>Не удалось загрузить клиники на карте.</p>
    <Button is-button text="Повторить" @click="refresh()"/>
  </div>
  <div
      v-else-if="mapsConfigured && data"
      class="clinic-map clinic-map__container"
      @pointerdown="enableViewportFilter"
      @wheel.passive="enableViewportFilter"
  >
    <div
        v-if="showSkeleton"
        class="clinic-map__skeleton"
        role="status"
        aria-live="polite"
        aria-busy="true"
    >
      <span class="visually-hidden">Загружаем карту...</span>
      <UISkeleton class="clinic-map__skeleton-fill"/>
    </div>
    <yandex-map
        v-model="map"
        :height="height"
        width="100%"
        :settings="BOOTSTRAP_MAP_SETTINGS"
    >
      <yandex-map-default-scheme-layer :settings="{theme: isDark ? 'dark' : 'light'}"/>
      <yandex-map-default-features-layer/>
      <yandex-map-controls :settings="{ position: 'right' }">
        <yandex-map-zoom-control/>
      </yandex-map-controls>
      <yandex-map-listener :settings="mapListenerSettings"/>
      <yandex-map-clusterer
          v-model="clusterer"
          zoom-on-cluster-click
      >
        <yandex-map-marker
            v-for="clinic in mappableClinics"
            :key="clinic.id"
            :settings="markerSettingsById[clinic.id]"
        >
          <button
              type="button"
              class="clinic-map__pin"
              :class="{'clinic-map__pin--selected': clinic.id === selectedClinicId}"
              :title="clinic.name"
          >
            <span class="visually-hidden">{{ clinic.name }}</span>
          </button>
        </yandex-map-marker>
        <template #cluster="{ length }">
          <div class="clinic-map__cluster fade-in">
            {{ length }}
          </div>
        </template>
      </yandex-map-clusterer>
      <yandex-map-marker v-if="userCoordinates" :settings="{coordinates: userCoordinates}">
        <div class="clinic-map__user" title="Вы здесь">
          <span class="visually-hidden">Ваше местоположение</span>
        </div>
      </yandex-map-marker>
    </yandex-map>
    <ClinicMapSheet
        v-if="openMarker"
        class="clinic-map__popup clinic-map__popup--overlay"
        :clinic="openMarker"
        :service-id="serviceId"
        @close="openMarker = null"
    />
  </div>
</template>

<script lang="ts" setup>

import {
  YandexMap,
  YandexMapClusterer,
  YandexMapControls,
  YandexMapDefaultFeaturesLayer,
  YandexMapDefaultSchemeLayer,
  YandexMapListener,
  YandexMapMarker,
  YandexMapZoomControl,
} from 'vue-yandex-maps';

import {computed, ref, shallowRef, watch} from 'vue';
import type {LngLat, YMap} from '@yandex/ymaps3-types';
import type {YMapClusterer} from '@yandex/ymaps3-types/packages/clusterer';
import type {TClinic, TClinicRaw} from "~/types/clinic"
import { omitClinicPhoneFromList } from '~/utils/omitClinicPhone'
import { isYandexMapsConfigured } from '~/utils/yandexMaps'
import {
  CITY_MAP_ZOOM,
  cityMapLocation,
  isSameMapCamera,
  listingMapLocation,
  nearbyMapLocation,
  type MapCamera,
} from '~/utils/clinicMapCamera'
import {
  clinicMapBoundsFromYandex,
  isSameClinicMapBounds,
  type ClinicMapBounds,
} from '~/utils/clinicMapBounds'

const {isDark} = useColorTheme();

/**
 * Виджет требует стабильный location на init. Реактивный city-центр в settings
 * сбрасывает кластер до пустого — пины вешаем, затем двигаем камеру setLocation.
 */
const BOOTSTRAP_MAP_SETTINGS = {
  location: {
    center: [37.337361, 44.863522] as [number, number],
    zoom: CITY_MAP_ZOOM,
  },
};

const mapsConfigured = computed(() => {
  const maps = (useRuntimeConfig().public as { yandexMaps?: { apikey?: string } }).yandexMaps;
  return isYandexMapsConfigured(maps?.apikey);
});

type YandexMapMarkerSettings = InstanceType<typeof YandexMapMarker>['settings'];

const props = defineProps({
  filters: {type: Object, required: true},
  view: {type: Boolean},
  serviceId: {type: Number},
  /** Клиника, выбранная в списке: карта центрируется на её пине. */
  selectedClinicId: {type: Number as unknown as () => number | null, default: null},
  userLocation: {type: Object as () => { lat: number; lng: number } | null, default: null},
  height: {type: String, default: '500px'},
});

const emit = defineEmits<{
  select: [clinicId: number];
  viewport: [bounds: ClinicMapBounds];
}>();
const map = shallowRef<YMap | null>(null);
const clusterer = shallowRef<YMapClusterer | null>(null);
const apiBaseUrl = useApiBaseUrl();
const openMarker = ref<TClinic | null>(null);

const {data, error, refresh} = await useFetch<{
  data: TClinicRaw[];
  cityLocation?: { lat: number; lng: number } | null;
}>('public/clinics/map', {
  baseURL: apiBaseUrl,
  headers: {
    Accept: 'application/json',
  },
  // computed, а не разложенный объект: со статическим query карта запрашивает клиник один раз
  // за монтирование и после смены фильтра показывает прежние метки.
  query: computed(() => ({...props.filters})),
  transform: (response) => ({...response, data: omitClinicPhoneFromList(response.data)}),
});

const mappableClinics = computed(() =>
    (data.value?.data ?? []).filter(
        (clinic): clinic is TClinic & { location: { latitude: number; longitude: number } } =>
            !!clinic.location?.longitude && !!clinic.location?.latitude
    )
);

const markerSettingsById = computed(() => {
  const settings: Record<number, YandexMapMarkerSettings> = {};

  for (const clinic of mappableClinics.value) {
    settings[clinic.id] = {
      coordinates: [clinic.location.longitude, clinic.location.latitude],
      onClick: () => handleMarkerClick(clinic),
    };
  }

  return settings;
});

let programmaticCameraUntil = 0;
let viewportTimer: ReturnType<typeof setTimeout> | null = null;
let allowViewportEmit = false;
const lastEmittedBounds = ref<ClinicMapBounds | null>(null);

const markProgrammaticCamera = (durationMs: number) => {
  programmaticCameraUntil = Date.now() + durationMs + 120;
  allowViewportEmit = false;
};

const enableViewportFilter = () => {
  allowViewportEmit = true;
};

const readMapBounds = () => {
  const instance = map.value as {bounds?: unknown; location?: {bounds?: unknown}} | null;
  return clinicMapBoundsFromYandex(instance?.bounds ?? instance?.location?.bounds);
};

const emitViewportFromMap = () => {
  if (!allowViewportEmit || Date.now() < programmaticCameraUntil || !map.value) {
    return;
  }

  const bounds = readMapBounds();
  if (!bounds || isSameClinicMapBounds(bounds, lastEmittedBounds.value)) {
    return;
  }

  lastEmittedBounds.value = bounds;
  emit('viewport', bounds);
};

const scheduleViewportEmit = () => {
  if (viewportTimer) {
    clearTimeout(viewportTimer);
  }

  viewportTimer = setTimeout(emitViewportFromMap, 280);
};

const mapListenerSettings = {
  onActionStart: () => {
    enableViewportFilter();
  },
  onActionEnd: () => {
    scheduleViewportEmit();
  },
  onUpdate: () => {
    scheduleViewportEmit();
  },
};

const centerMapOnMarker = (clinic: TClinic) => {
  if (!map.value || !clinic.location) return;

  const zoomLevel = map.value.zoom || 15;
  markProgrammaticCamera(300);

  map.value.setLocation({
    center: [clinic.location.longitude, clinic.location.latitude],
    zoom: zoomLevel,
    duration: 300
  });
};

const handleMarkerClick = (clinic: TClinic) => {
  openMarker.value = clinic;
  centerMapOnMarker(clinic);
  emit('select', clinic.id);
};

const userCoordinates = computed<LngLat | null>(() =>
    props.userLocation ? [props.userLocation.lng, props.userLocation.lat] : null);

const cameraLocation = computed(() => {
  if (props.userLocation) {
    return nearbyMapLocation(props.userLocation);
  }

  if (data.value?.cityLocation) {
    return cityMapLocation(data.value.cityLocation);
  }

  const coordinates = mappableClinics.value.map((clinic) =>
      [clinic.location.longitude, clinic.location.latitude] as [number, number],
  );

  return listingMapLocation(coordinates);
});

const mapReady = ref(false);
watch(map, (instance) => {
  if (instance) {
    mapReady.value = true;
  }
});
const showSkeleton = computed(() => !mapReady.value);

// Выбор в списке открывает тот же шит и ведёт карту к пину.
watch(() => props.selectedClinicId, (clinicId) => {
  if (!clinicId) {
    return;
  }

  const clinic = mappableClinics.value.find((item) => item.id === clinicId);
  if (!clinic) {
    return;
  }

  if (openMarker.value?.id !== clinic.id) {
    openMarker.value = clinic;
  }
  centerMapOnMarker(clinic);
});

const lastAppliedCamera = ref<MapCamera | null>(null);
const applyMapCamera = () => {
  const next = cameraLocation.value;
  if (!map.value || !next || isSameMapCamera(next, lastAppliedCamera.value)) {
    return;
  }

  const duration = lastAppliedCamera.value ? 300 : 0;
  lastAppliedCamera.value = next;
  markProgrammaticCamera(duration);
  map.value.setLocation({...next, duration});
};

watch([map, cameraLocation], applyMapCamera);

watch(data, () => {
  openMarker.value = null;
});
</script>

<style lang="scss">
@use "~/assets/css/components/clinic-map.scss";
</style>
